import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Stethoscope, Check, ArrowLeft, ClipboardList, MapPin, Printer,
  ShieldAlert, Sun, Moon, Pill, Droplet, Sparkles, Cloud, Sunrise,
} from "lucide-react";
import { QUESTIONS, recommendProtocol } from "@/lib/products";
import DemographicsFields from "@/components/DemographicsFields";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON = {
  droplet: Droplet,
  sparkle: Sparkles,
  cloud: Cloud,
  sun: Sunrise,
};

export default function RegimenBuilder() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [demo, setDemo] = useState({ gender: "", age: "", city: "", location: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [unlocked, setUnlocked] = useState(false);

  const quizDone = step >= QUESTIONS.length;
  const isResult = quizDone && unlocked;
  const isGate = quizDone && !unlocked;
  const protocol = useMemo(() => (isResult ? recommendProtocol(answers) : null), [isResult, answers]);

  const choose = (qid, value) => {
    setAnswers((a) => ({ ...a, [qid]: value }));
    setStep((s) => s + 1);
  };

  const profileText = () =>
    QUESTIONS.map((q) => {
      const opt = q.options.find((o) => o.value === answers[q.id]);
      return `${q.q} ${opt ? opt.label : "-"}`;
    }).join(" | ");

  const submitGate = async () => {
    if (contact.name.trim().length < 2) return toast.error("Please enter your name.");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(contact.email)) return toast.error("Please enter a valid email.");
    if (contact.phone.replace(/\D/g, "").length < 7) return toast.error("Please enter a valid mobile number.");
    setSubmitting(true);
    try {
      await axios.post(`${API}/protocol-request`, {
        name: contact.name.trim(),
        email: contact.email.trim(),
        phone: contact.phone.trim(),
        products: [],
        profile: profileText(),
        gender: demo.gender || null,
        age: demo.age ? Number(demo.age) : null,
        city: demo.city || null,
        location: demo.location || null,
      });
      setUnlocked(true);
    } catch {
      toast.error("Couldn't save your details. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const submit = async () => {
    // Called from the printed-brochure form after results are already unlocked.
    if (contact.name.trim().length < 2) return toast.error("Please enter your name.");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(contact.email)) return toast.error("Please enter a valid email.");
    if (contact.phone.replace(/\D/g, "").length < 7) return toast.error("Please enter a valid mobile number.");
    setSubmitting(true);
    try {
      await axios.post(`${API}/protocol-request`, {
        name: contact.name.trim(),
        email: contact.email.trim(),
        phone: contact.phone.trim(),
        products: [],
        profile: profileText() + " · Requested printed brochure",
        gender: demo.gender || null,
        age: demo.age ? Number(demo.age) : null,
        city: demo.city || null,
        location: demo.location || null,
      });
      setSubmitted(true);
      toast.success("Your enquiry has reached JAKS Pharma.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const restart = () => {
    setAnswers({});
    setContact({ name: "", email: "", phone: "" });
    setDemo({ gender: "", age: "", city: "", location: "" });
    setSubmitted(false);
    setUnlocked(false);
    setStep(0);
  };

  const progress = Math.min(100, Math.round((Math.min(step, QUESTIONS.length) / QUESTIONS.length) * 100));

  return (
    <main className="pt-[68px] min-h-screen" data-testid="regimen-page">
      {/* Header */}
      <section className="relative z-10 bg-[color:var(--relumia-ink)] text-[color:var(--relumia-base)] py-16 md:py-20">
        <div className="mx-auto max-w-[1000px] px-5 md:px-10">
          <div className="flex items-center gap-3 mb-5">
            <Stethoscope size={18} className="text-gold" strokeWidth={1.6} />
            <span className="overline !text-gold">Clinical Framework</span>
          </div>
          <h1 className="font-serif font-light tracking-tight leading-[1.02] text-4xl md:text-6xl">
            Clinical Skin <span className="italic text-gold">Assessment</span>
          </h1>
          <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-white/70">
            A five-step guided assessment. At the end you'll see a generic, dermatologist-friendly
            skincare framework tailored to your responses — plus optional companions from the JAKS
            Pharma range to discuss with your clinician.
          </p>
        </div>
      </section>

      <section className="relative z-10 py-14 md:py-20">
        <div className="mx-auto max-w-[1000px] px-5 md:px-10">
          {/* Progress */}
          {!quizDone && (
            <div className="mb-10">
              <div className="flex items-center justify-between mb-3">
                <span className="overline">Step {step + 1} of {QUESTIONS.length}</span>
                <span className="text-[12px] text-[color:var(--relumia-muted)]">{progress}% complete</span>
              </div>
              <div className="h-1 rounded-full bg-champagne overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress}%`, background: "linear-gradient(to right,#c5a059,#a9863f)" }} />
              </div>
            </div>
          )}

          {/* Questions */}
          {!quizDone && (
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                data-testid={`quiz-step-${step}`}
              >
                <h2 className="font-serif font-light tracking-tight text-3xl md:text-4xl text-[color:var(--relumia-ink)] max-w-2xl">
                  {QUESTIONS[step].q}
                </h2>
                <p className="mt-2 text-[13.5px] text-[color:var(--relumia-muted)]">{QUESTIONS[step].help}</p>
                <div className="mt-8 grid sm:grid-cols-2 gap-4">
                  {QUESTIONS[step].options.map((o) => {
                    const active = answers[QUESTIONS[step].id] === o.value;
                    return (
                      <button
                        key={o.value}
                        onClick={() => choose(QUESTIONS[step].id, o.value)}
                        data-testid={`quiz-option-${o.value}`}
                        className={`group text-left rounded-sm border px-5 py-5 transition-all ${
                          active
                            ? "border-[color:var(--relumia-gold)] bg-surface"
                            : "border-champagne bg-base hover:border-[color:var(--relumia-gold)]/60 hover:bg-surface"
                        }`}
                      >
                        <span className="flex items-center justify-between gap-4">
                          <span className="text-[14.5px] text-[color:var(--relumia-ink)]">{o.label}</span>
                          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-[color:var(--relumia-gold)]/40 text-gold opacity-0 group-hover:opacity-100 transition-opacity">
                            →
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
                {step > 0 && (
                  <button onClick={() => setStep((s) => s - 1)} data-testid="quiz-back" className="mt-8 inline-flex items-center gap-2 text-[13px] text-[color:var(--relumia-muted)] hover:text-[color:var(--relumia-ink)] transition-colors">
                    <ArrowLeft size={15} /> Back
                  </button>
                )}
              </motion.div>
            </AnimatePresence>
          )}

          {/* Gate — collect contact + demographics BEFORE showing the framework */}
          {isGate && (
            <div data-testid="regimen-gate" className="max-w-[720px]">
              <div className="flex items-center gap-3 mb-3">
                <ClipboardList size={18} className="text-gold" strokeWidth={1.6} />
                <span className="overline">One last step</span>
              </div>
              <h2 className="font-serif font-light tracking-tight text-3xl md:text-4xl text-[color:var(--relumia-ink)]">
                Almost there — a few details before we <span className="italic text-gold">personalise your framework.</span>
              </h2>
              <p className="mt-4 text-[13.5px] leading-relaxed text-[color:var(--relumia-muted)] max-w-lg">
                Your name, contact and a bit of context help our team follow up with your dermatologist-friendly framework and any dermatologist consultation nearby.
              </p>

              <div className="mt-8 grid sm:grid-cols-3 gap-4">
                <input data-testid="gate-name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} placeholder="Full name*" className="bg-transparent border-b border-[color:var(--relumia-ink)]/25 py-2.5 text-[14px] text-[color:var(--relumia-ink)] placeholder:text-[color:var(--relumia-muted)] focus:outline-none focus:border-[color:var(--relumia-gold)]" />
                <input data-testid="gate-email" type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder="Email*" className="bg-transparent border-b border-[color:var(--relumia-ink)]/25 py-2.5 text-[14px] text-[color:var(--relumia-ink)] placeholder:text-[color:var(--relumia-muted)] focus:outline-none focus:border-[color:var(--relumia-gold)]" />
                <input data-testid="gate-phone" type="tel" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} placeholder="Mobile number*" className="bg-transparent border-b border-[color:var(--relumia-ink)]/25 py-2.5 text-[14px] text-[color:var(--relumia-ink)] placeholder:text-[color:var(--relumia-muted)] focus:outline-none focus:border-[color:var(--relumia-gold)]" />
              </div>

              <DemographicsFields values={demo} onChange={setDemo} tidPrefix="gate-demo" />

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <button
                  onClick={submitGate}
                  disabled={submitting}
                  data-testid="gate-submit"
                  className="inline-flex items-center gap-2 rounded-full bg-[color:var(--relumia-ink)] px-8 py-3.5 text-[13px] tracking-wide text-white hover:bg-[color:var(--relumia-gold-deep)] transition-colors disabled:opacity-60"
                >
                  {submitting ? "Preparing your framework…" : "See my framework"}
                </button>
                <button onClick={() => setStep(QUESTIONS.length - 1)} data-testid="gate-back" className="inline-flex items-center gap-2 text-[13px] text-[color:var(--relumia-muted)] hover:text-[color:var(--relumia-ink)] transition-colors">
                  <ArrowLeft size={15} /> Back to questions
                </button>
              </div>
              <p className="mt-5 text-[11px] leading-relaxed text-[color:var(--relumia-muted)]">
                By continuing you agree to receive skincare updates from JAKS Pharma. Your details are used only to send this framework and any relevant clinician information.
              </p>
            </div>
          )}

          {/* Results */}
          {isResult && protocol && (
            <div data-testid="regimen-result" className="print-area">
              <div className="flex items-center gap-3 mb-3">
                <ClipboardList size={18} className="text-gold" strokeWidth={1.6} />
                <span className="overline">Your Clinical Framework</span>
              </div>
              <h2 className="font-serif font-light tracking-tight text-3xl md:text-5xl text-[color:var(--relumia-ink)]">
                A dermatologist-friendly <span className="italic text-gold">framework</span>.
              </h2>
              <p className="mt-4 max-w-2xl text-[14.5px] leading-relaxed text-[color:var(--relumia-muted)]">
                Below is a generic, ingredient-led skincare framework based on your responses.
                It is <em>not</em> a prescription — take it to your dermatologist to personalise the
                exact molecules, strengths and frequency for your skin.
              </p>

              {/* Relumia hero — only when brightening goals */}
              {protocol.hero.show && (
                <div
                  data-testid="relumia-hero"
                  className="mt-10 relative overflow-hidden rounded-sm border border-[color:var(--relumia-gold)]/40 bg-gradient-to-br from-[#fdf7ea] via-[#f7ecd0] to-[#f0dfae] p-7 md:p-9"
                >
                  <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[color:var(--relumia-gold)]/25 blur-3xl" />
                  <div className="relative flex items-start justify-between gap-6 flex-wrap">
                    <div className="max-w-xl">
                      <div className="overline !text-[color:var(--relumia-gold-deep)] mb-2">Featured · From Within</div>
                      <div className="font-serif text-3xl md:text-4xl text-[color:var(--relumia-ink)]">
                        {protocol.hero.product.name}<span className="text-gold">™</span>
                      </div>
                      <p className="mt-3 text-[14px] leading-relaxed text-[color:var(--relumia-ink)]/80">
                        {protocol.hero.pitch}
                      </p>
                      <div className="mt-5 flex flex-wrap gap-2">
                        {["OPITAC™ Japanese Glutathione", "Vitamin C · D3", "Zinc", "Curcumin"].map((t) => (
                          <span key={t} className="rounded-full bg-white/70 border border-[color:var(--relumia-gold)]/40 px-3 py-1 text-[11.5px] text-[color:var(--relumia-ink)] tracking-wide">
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                    <a href="/#contact" data-testid="relumia-hero-cta" className="shrink-0 inline-flex items-center gap-2 rounded-full bg-[color:var(--relumia-ink)] px-6 py-3 text-[13px] tracking-wide text-white hover:bg-[color:var(--relumia-gold-deep)] transition-colors">
                      Enquire about Relumia
                    </a>
                  </div>
                </div>
              )}

              {/* Framework steps */}
              <div className="mt-10 space-y-4">
                <div className="overline mb-2">Topical framework</div>
                {protocol.steps.map((s, i) => {
                  const Icon = ICON[s.icon] || Droplet;
                  return (
                    <div key={s.key} data-testid={`framework-step-${s.key}`} className="grid md:grid-cols-[auto_1fr_auto] gap-5 md:gap-8 items-start bg-base border border-champagne rounded-sm p-6 md:p-7">
                      <div className="flex items-center gap-3 md:flex-col md:items-start md:gap-2">
                        <div className="font-serif text-3xl text-gold leading-none">0{i + 1}</div>
                        <Icon size={20} className="text-gold" strokeWidth={1.6} />
                      </div>
                      <div>
                        <div className="flex items-baseline gap-3 flex-wrap">
                          <div className="font-serif text-2xl text-[color:var(--relumia-ink)]">{s.title}</div>
                          <div className="text-[11.5px] tracking-wide uppercase text-[color:var(--relumia-muted)]">{s.time}</div>
                        </div>
                        <p className="mt-2 text-[13.5px] leading-relaxed text-[color:var(--relumia-ink)]/85">{s.guidance}</p>
                        {s.jaks && (
                          <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-[color:var(--relumia-gold)]/40 bg-surface px-3.5 py-1.5">
                            <span className="text-[10.5px] tracking-widest uppercase text-[color:var(--relumia-gold-deep)]">From JAKS</span>
                            <span className="text-[12.5px] text-[color:var(--relumia-ink)]">{s.jaks.name}</span>
                          </div>
                        )}
                        {s.jaks?.note && (
                          <p className="mt-2 text-[11.5px] text-[color:var(--relumia-muted)]">{s.jaks.note}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Regimen schedule */}
              <div className="mt-10 grid sm:grid-cols-3 gap-5">
                {[
                  [Sun, "Morning", protocol.regimen.am],
                  [Moon, "Evening", protocol.regimen.pm],
                  [Pill, "From Within", protocol.regimen.oral],
                ].map(([Icon, title, steps]) => (
                  steps.length > 0 && (
                    <div key={title} className="bg-surface border border-champagne rounded-sm p-6">
                      <div className="flex items-center gap-2 mb-4"><Icon size={16} className="text-gold" strokeWidth={1.6} /><span className="overline">{title}</span></div>
                      <ol className="space-y-2">
                        {steps.map((s, i) => (
                          <li key={i} className="text-[13px] leading-snug text-[color:var(--relumia-ink)] flex gap-2">
                            <span className="text-gold font-serif">{i + 1}.</span> {s}
                          </li>
                        ))}
                      </ol>
                    </div>
                  )
                ))}
              </div>

              {/* Notes */}
              {protocol.notes.length > 0 && (
                <div className="mt-8 bg-base border border-champagne rounded-sm p-6">
                  <div className="overline mb-3">Clinical notes</div>
                  <ul className="space-y-2">
                    {protocol.notes.map((n, i) => (
                      <li key={i} className="text-[13px] leading-relaxed text-[color:var(--relumia-muted)] flex gap-2"><span className="text-gold">✦</span>{n}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Disclaimer */}
              <div className="mt-8 flex items-start gap-3 rounded-sm bg-surface border border-champagne p-5">
                <ShieldAlert size={18} className="text-gold shrink-0 mt-0.5" strokeWidth={1.6} />
                <p className="text-[12.5px] leading-relaxed text-[color:var(--relumia-muted)]">
                  This framework is for educational purposes and does <strong>not</strong> constitute a
                  medical diagnosis or prescription. Any specific product, molecule or dosage should
                  be confirmed with a qualified dermatologist or your healthcare provider.
                </p>
              </div>

              {/* Enquire form (optional, softer copy) */}
              {!submitted ? (
                <div className="mt-10 bg-[color:var(--relumia-ink)] rounded-sm p-6 md:p-8 no-print" data-testid="protocol-request-form">
                  <div className="overline !text-gold mb-2">Want a physical brochure?</div>
                  <p className="text-white/70 text-[13px] leading-relaxed mb-5 max-w-xl">
                    Leave your details and JAKS Pharma will send you a printed reference of this
                    framework and product information for your dermatologist visit.
                  </p>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <input data-testid="protocol-name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} placeholder="Full name*" className="bg-transparent border-b border-white/25 py-2.5 text-[14px] text-white placeholder:text-white/50 focus:outline-none focus:border-[color:var(--relumia-gold)]" />
                    <input data-testid="protocol-email" type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder="Email*" className="bg-transparent border-b border-white/25 py-2.5 text-[14px] text-white placeholder:text-white/50 focus:outline-none focus:border-[color:var(--relumia-gold)]" />
                    <input data-testid="protocol-phone" type="tel" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} placeholder="Mobile number*" className="bg-transparent border-b border-white/25 py-2.5 text-[14px] text-white placeholder:text-white/50 focus:outline-none focus:border-[color:var(--relumia-gold)]" />
                  </div>
                  <button onClick={submit} disabled={submitting} data-testid="protocol-submit" className="mt-7 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-[13px] tracking-wide text-[color:var(--relumia-ink)] hover:bg-[color:var(--relumia-gold)] hover:text-white transition-colors disabled:opacity-60">
                    {submitting ? "Sending…" : "Send me the brochure"}
                  </button>
                </div>
              ) : (
                <div className="mt-10 bg-surface border border-champagne rounded-sm p-6 flex items-center gap-3" data-testid="protocol-submitted">
                  <Check size={20} className="text-gold" /> <span className="text-[14px] text-[color:var(--relumia-ink)]">Thank you — our team will reach out shortly.</span>
                </div>
              )}

              {/* Actions */}
              <div className="mt-6 flex flex-wrap items-center gap-4 no-print">
                <Link to="/find-a-dermatologist" data-testid="find-clinic-cta" className="inline-flex items-center gap-2 rounded-full bg-[color:var(--relumia-ink)] px-6 py-3 text-[13px] tracking-wide text-white hover:bg-[color:var(--relumia-gold-deep)] transition-colors">
                  <MapPin size={15} /> Find a Dermatologist
                </Link>
                <button onClick={() => window.print()} data-testid="print-protocol" className="inline-flex items-center gap-2 rounded-full border border-[color:var(--relumia-ink)]/25 px-6 py-3 text-[13px] tracking-wide text-[color:var(--relumia-ink)] hover:border-[color:var(--relumia-gold)] transition-colors">
                  <Printer size={15} /> Download for my Doctor
                </button>
                <button onClick={restart} data-testid="restart-quiz" className="text-[13px] text-gold hover:text-[color:var(--relumia-gold-deep)] transition-colors">
                  Retake assessment
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
